import {
  Add,
  Delete,
  UploadFile,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";

import {
  useProductTypeKeywords,
  useProductTypeKeywordsMutations,
} from "../../hooks/usePricing";
import { ProductTypeKeywords } from "../../types/admin";
import AdminPage from "./AdminPage";

const GroupCard: React.FC<{
  item: ProductTypeKeywords;
  onKeywordsChange: (id: number, keywords: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onUpload: (id: number, file: File) => Promise<void>;
}> = ({ item, onKeywordsChange, onDelete, onUpload }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newKw, setNewKw] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const keywords = item.keywords
    ? item.keywords.split(",").map((k) => k.trim()).filter(Boolean)
    : [];

  const saveKeywords = async (updated: string[]) => {
    setSaving(true);
    try {
      await onKeywordsChange(item.id, updated.join(", "));
    } finally {
      setSaving(false);
    }
  };

  const handleAddKeyword = async () => {
    const trimmed = newKw.trim();
    if (!trimmed || keywords.includes(trimmed)) return;
    setNewKw("");
    await saveKeywords([...keywords, trimmed]);
  };

  const handleRemoveKeyword = async (kw: string) => {
    await saveKeywords(keywords.filter((k) => k !== kw));
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm(t("admin.pricing.confirmDeleteKeywordGroup", `Delete the "${item.type}" keyword group?`))) return;
    setDeleting(true);
    try {
      await onDelete(item.id);
    } finally {
      setDeleting(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(item.id, file);
      toast.success(t("admin.pricing.keywordsUploaded", "Keywords uploaded successfully"));
    } catch {
      toast.error(t("admin.pricing.uploadFailed", "Failed to upload keywords"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {item.type}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            {keywords.length} {t("admin.pricing.keywordsCount", "keywords")}
          </Typography>
        }
        action={
          <Box sx={{ display: "flex", gap: 1, mr: 1 }}>
            <Tooltip title={t("admin.pricing.uploadKeywords", "Upload Keywords (CSV)")}>
              <span>
                <IconButton
                  size="small"
                  color="info"
                  onClick={handleUploadClick}
                  disabled={uploading}
                >
                  {uploading ? <CircularProgress size={20} /> : <UploadFile />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("admin.pricing.deleteGroup", "Delete group")}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDeleteGroup}
                  disabled={deleting}
                >
                  {deleting ? <CircularProgress size={20} /> : <Delete />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        }
      />
      <Divider />
      <CardContent>
        {/* Keyword chips */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2, minHeight: 36 }}>
          {keywords.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              {t("admin.pricing.noKeywords", "No keywords yet. Add one below.")}
            </Typography>
          ) : (
            keywords.map((kw) => (
              <Chip
                key={kw}
                label={kw}
                size="small"
                variant="outlined"
                onDelete={saving ? undefined : () => handleRemoveKeyword(kw)}
              />
            ))
          )}
          {saving && <CircularProgress size={18} sx={{ alignSelf: "center" }} />}
        </Box>

        {/* Add keyword input */}
        <TextField
          size="small"
          placeholder={t("admin.pricing.addKeywordPlaceholder", "Add keyword...")}
          value={newKw}
          onChange={(e) => setNewKw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddKeyword();
            }
          }}
          disabled={saving}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={t("admin.pricing.addKeyword", "Add keyword")}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleAddKeyword}
                      disabled={!newKw.trim() || saving}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{ width: 280 }}
        />
      </CardContent>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".csv"
        onChange={handleFileChange}
      />
    </Card>
  );
};

const ProductTypeKeywordsPage: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useProductTypeKeywords();
  const { updateKeywords, uploadKeywords, deleteKeywords } = useProductTypeKeywordsMutations();

  const items: ProductTypeKeywords[] = data?.data || [];

  const handleKeywordsChange = async (id: number, keywords: string) => {
    await updateKeywords.mutateAsync({ id, data: { keywords } });
  };

  const handleDelete = async (id: number) => {
    await deleteKeywords.mutateAsync(id);
  };

  const handleUpload = async (id: number, file: File) => {
    await uploadKeywords.mutateAsync({ id, file });
  };

  return (
    <AdminPage>
      <Container maxWidth="md">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t("admin.pricing.productTypeKeywords", "Product Type Keywords")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t(
              "admin.pricing.productTypeKeywordsSubtitle",
              "Keyword lists used to classify product types during shipping cost calculation."
            )}
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="body1" color="text.secondary">
              {t("admin.pricing.noProductTypeKeywords", "No product type keywords found.")}
            </Typography>
          </Box>
        ) : (
          items.map((item) => (
            <GroupCard
              key={item.id}
              item={item}
              onKeywordsChange={handleKeywordsChange}
              onDelete={handleDelete}
              onUpload={handleUpload}
            />
          ))
        )}
      </Container>
    </AdminPage>
  );
};

export default ProductTypeKeywordsPage;
